import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * ChartFrame — a tiny wrapper that defers rendering Recharts children until
 * the parent div reports a non-zero width. This prevents Recharts'
 * ResponsiveContainer from firing its "width(-1) height(-1)" console warning
 * during the first synchronous render pass (before layout is measured).
 *
 * Usage:
 *   <ChartFrame className="h-56">
 *     <ResponsiveContainer width="100%" height="100%">
 *       <BarChart …/>
 *     </ResponsiveContainer>
 *   </ChartFrame>
 */
export default function ChartFrame({ children, className = "", testid, fallback = null }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      const w = el.clientWidth || 0;
      const h = el.clientHeight || 0;
      if (w > 0 && h > 0) setReady(true);
    };
    check();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(check);
      ro.observe(el);
      return () => ro.disconnect();
    }
    // Fallback: try again after one frame (older browsers)
    const id = window.requestAnimationFrame(check);
    return () => window.cancelAnimationFrame(id);
  }, []);

  // Safety net — if for any reason the observer never fires, mount after 100ms
  useEffect(() => {
    if (ready) return;
    const t = setTimeout(() => setReady(true), 150);
    return () => clearTimeout(t);
  }, [ready]);

  return (
    <div ref={ref} className={className} data-testid={testid}>
      {ready ? children : fallback}
    </div>
  );
}
